import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JsonValue } from '@backstage/types';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Content, ErrorPanel, Progress } from '@backstage/core-components';
import {
  SecretsContextProvider,
  scaffolderApiRef,
} from '@backstage/plugin-scaffolder-react';
import { EmbeddableWorkflow } from '@backstage/plugin-scaffolder-react/alpha';
import { GithubAccessStatus } from '../../scaffolder/GithubAccessStatus';
import { GITOPS_REPO_DEFAULT } from '../../scaffolder/githubOrgAccess';
import { useGithubAccess } from '../../scaffolder/useGithubAccess';

const TEMPLATE_NAMESPACE = 'default';
const TEMPLATE_NAME = 'modify-internal-app';

/**
 * "Modify" tab for an App XR entity. Fetches the committed XR's editable fields
 * from the internal-apps backend route and renders the modify-internal-app
 * scaffolder template prefilled with them (read == write target). On submit it
 * creates the scaffolder task and navigates to its run page.
 *
 * The PR step runs with the user's GitHub token, so before the form (and again
 * on submit) we check that token can actually see the gitops repository: on
 * hulkag, which enforces SAML SSO, a token that was never authorized for the
 * org fails deep in the run with a bare 404. See useGithubAccess.
 */
export const ModifyAppContent = () => {
  const { entity } = useEntity();
  const name = entity.metadata.name;

  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const navigate = useNavigate();
  const gitopsRepo =
    configApi.getOptionalString('internalApps.gitopsRepo') ??
    GITOPS_REPO_DEFAULT;

  const [initialState, setInitialState] = useState<
    Record<string, JsonValue> | undefined
  >();
  const [error, setError] = useState<Error | undefined>();

  // Checked up front so the problem shows above the form, not after filling
  // it in, and again inside onCreate so a stale token cannot reach the run.
  const {
    state,
    access,
    error: accessError,
    problem,
    authorizeUrl,
    authorize,
  } = useGithubAccess({ targets: [gitopsRepo], auto: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('internal-apps');
        const res = await fetchApi.fetch(
          `${baseUrl}/xr/${encodeURIComponent(name)}`,
        );
        if (!res.ok) {
          throw new Error(
            `Failed to load current config for "${name}" (${res.status})`,
          );
        }
        const data = (await res.json()) as {
          values: Record<string, JsonValue>;
          blobSha: string;
        };
        if (!cancelled) {
          setInitialState({ ...data.values, name, blobSha: data.blobSha });
        }
      } catch (e: any) {
        if (!cancelled) setError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, name]);

  const onCreate = useCallback(
    async (values: Record<string, JsonValue>) => {
      const token = await authorize();
      const { taskId } = await scaffolderApi.scaffold({
        templateRef: stringifyEntityRef({
          kind: 'Template',
          namespace: TEMPLATE_NAMESPACE,
          name: TEMPLATE_NAME,
        }),
        values,
        secrets: { USER_OAUTH_TOKEN: token },
      });
      navigate(`/create/tasks/${taskId}`);
    },
    [scaffolderApi, authorize, navigate],
  );

  if (error) {
    return (
      <Content>
        <ErrorPanel title="Couldn't open the modify form" error={error} />
      </Content>
    );
  }
  if (!initialState) {
    return <Progress />;
  }

  return (
    <Content>
      <div style={{ padding: '4px 0 12px' }}>
        <GithubAccessStatus
          state={state}
          access={access}
          error={accessError}
          problem={problem}
          authorizeUrl={authorizeUrl}
          targets={[gitopsRepo]}
          onRetry={o => authorize(o).catch(() => undefined)}
          okMessage={`GitHub access to ${gitopsRepo} authorized — the pull request will be opened as you.`}
        />
      </div>
      <SecretsContextProvider>
        <EmbeddableWorkflow
          title={`Modify ${name}`}
          description="Change an allowed subset of settings. Opens a PR to internal-apps-gitops; platformVersion, crossplane.*, secrets, and unmodelled fields are preserved."
          namespace={TEMPLATE_NAMESPACE}
          templateName={TEMPLATE_NAME}
          initialState={initialState}
          extensions={[]}
          onCreate={onCreate}
          onError={e => (e ? <ErrorPanel error={e} /> : null)}
        />
      </SecretsContextProvider>
    </Content>
  );
};
